import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSupportContext } from '../../context/SupportContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users,
  Pencil,
  Trash2,
  UserPlus,
  Upload,
  Search,
  Eye,
  X,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import EditUserDrawer from './EditUserDrawer';
import ImportUsersModal from './ImportUsersModal';
import AddUserModal from './AddUserModal';
import { cachedFetch, fetchCacheKey, invalidateFetchCache } from '../../utils/fetchCache';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Shared agency users table + actions (Admin Control / Agency Detail parity).
 *
 * @param {string} agencyId - Agency tenant id
 * @param {string} [agencyName] - Display name for modals
 * @param {'staff-portal'|'owner-console'} [listSource='staff-portal'] - Users list API
 * @param {boolean} [enableSupportClient=true] - Show View-as-client action
 * @param {string} [pageTitle='Users'] - Page heading
 * @param {(payload: {total: number, user_stats?: object}) => void} [onUsersLoaded]
 */
const AgencyUsersManagement = ({
  agencyId,
  agencyName = '',
  listSource = 'staff-portal',
  enableSupportClient = true,
  pageTitle = 'Users',
  onUsersLoaded,
}) => {
  const { token } = useAuth();
  const { enterClientSupport, isLoading: contextLoading } = useSupportContext();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [editUserDrawerOpen, setEditUserDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const usersCachePrefix = fetchCacheKey('agency-users', agencyId, listSource);

  const fetchUsers = useCallback(
    async (statusParam = null, { force = false } = {}) => {
      if (!token || !agencyId) return;

      const effectiveStatus = statusParam !== null ? statusParam : statusFilter;
      const cacheKey = fetchCacheKey(usersCachePrefix, effectiveStatus);

      setLoading(true);
      try {
        const usersParams = new URLSearchParams();
        usersParams.append('limit', '100');
        if (effectiveStatus && effectiveStatus !== 'all') {
          usersParams.append('status', effectiveStatus);
        }

        const listUrl =
          listSource === 'owner-console'
            ? `${API}/owner-console/agencies/${agencyId}/users?${usersParams.toString()}`
            : `${API}/portal/staff/users?${usersParams.toString()}`;

        const data = await cachedFetch(
          cacheKey,
          async () => {
            const response = await axios.get(listUrl, {
              headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
          },
          { force }
        );

        setUsers(data.users || data.items || []);
        const stats = data.user_stats || null;
        const nextTotal =
          typeof data.total === 'number'
            ? data.total
            : (stats?.total_users ?? stats?.total ?? (data.users || data.items || []).length);
        if (typeof onUsersLoaded === 'function') {
          onUsersLoaded({
            total: nextTotal,
            user_stats: stats
              ? {
                  ...stats,
                  total: stats.total_users ?? stats.total ?? nextTotal,
                }
              : null,
          });
        }
      } catch (err) {
        console.error('Failed to fetch agency users:', err);
        toast.error(err.response?.data?.detail || 'Failed to load users');
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [token, agencyId, listSource, statusFilter, usersCachePrefix, onUsersLoaded]
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (token && agencyId && !loading) {
      fetchUsers(statusFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const refreshUsers = useCallback(() => {
    invalidateFetchCache(usersCachePrefix);
    fetchUsers(statusFilter, { force: true });
  }, [usersCachePrefix, fetchUsers, statusFilter]);

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.phone?.toLowerCase().includes(searchLower) ||
        user.user_id?.toLowerCase().includes(searchLower);

      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [users, searchQuery, roleFilter]);

  const handleSupportClient = async (userId, userName) => {
    try {
      const result = await enterClientSupport(userId, `Supporting client: ${userName}`);
      toast.success(`Viewing as: ${userName}`);

      // Prefer backend canonical redirect_path — never build /agency/${account_id}/...
      const redirectPath = result?.redirect_path || result?.redirect_to;
      if (redirectPath && String(redirectPath).startsWith('/')) {
        console.info('[SUPPORT_CLIENT][LANDING]', {
          target_user_id: userId,
          account_type: result?.account_type || null,
          portal_type: result?.portal_type || result?.effective_portal_type || null,
          agency_id: result?.agency_id ?? null,
          effective_agency_id: result?.effective_agency_id ?? null,
          redirect_path: redirectPath,
        });
        window.location.href = redirectPath;
        return;
      }

      const portalType = result?.effective_portal_type || result?.portal_type;
      if (portalType === 'managed_client_portal') {
        window.location.href = '/client/dashboard';
      } else if (
        portalType === 'self_repair'
        || portalType === 'self_serve_portal'
      ) {
        window.location.href = '/dashboard';
      } else if (portalType === 'staff_portal') {
        // Agency staff support — only if backend provided a real agency id.
        const agencyId = result?.effective_agency_id || result?.agency_id;
        if (agencyId) {
          window.location.href = `/agency/${agencyId}/dashboard`;
        } else {
          window.location.href = '/staff/dashboard';
        }
      } else {
        window.location.href = '/dashboard';
      }
    } catch (err) {
      toast.error('Failed to enter client support mode');
      console.error(err);
    }
  };

  const handleDeleteUser = async (user) => {
    try {
      const response = await axios.delete(`${API}/admin/users/${user.user_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const result = response.data;
      if (result.success) {
        if (result.action === 'hard_delete') {
          toast.success(`User ${user.name || user.email} permanently deleted`);
        } else {
          toast.success(result.message || `User ${user.name || user.email} archived`);
        }
        refreshUsers();
        setDeleteConfirmOpen(false);
        setUserToDelete(null);
      } else {
        toast.error(result.message || 'Delete operation failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user. Please try again.');
    }
  };

  const handleBulkDelete = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/users/bulk-delete`,
        {
          user_ids: selectedUsers.map((u) => u.user_id),
          hard_delete: false,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const result = response.data;
      const successCount = result.deleted + result.archived;
      const failCount = result.blocked + result.failed;

      if (successCount > 0 && failCount === 0) {
        toast.success(`${successCount} user(s) processed successfully`);
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(`${successCount} succeeded, ${failCount} failed.`);
        result.details
          .filter((d) => !d.success)
          .forEach((d) => {
            toast.error(`${d.email || d.user_id}: ${d.message}`, { duration: 5000 });
          });
      } else if (failCount > 0) {
        result.details.forEach((d) => {
          toast.error(`${d.email || d.user_id}: ${d.message}`, { duration: 5000 });
        });
      }

      refreshUsers();
      setSelectedUsers([]);
      setBulkDeleteOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed. Please try again.');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setRoleFilter('all');
    setStatusFilter('all');
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agency-users-management">
      <div>
        <h1 className="text-2xl font-bold text-stone-900">{pageTitle}</h1>
        <p className="text-slate-500 mt-1">Manage users and clients in your agency</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-40 h-9 bg-white" data-testid="role-filter">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="owner">Agency Owner</SelectItem>
                <SelectItem value="specialist">Specialist</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="self_repair_customer">Self-Repair Customer</SelectItem>
                <SelectItem value="managed_client">Managed Client</SelectItem>
              </SelectContent>
            </Select>

            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search name, email, phone..."
                className="pl-9 h-9 bg-white"
                data-testid="users-search"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32 h-9 bg-white" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>

            {(searchQuery || roleFilter !== 'all' || statusFilter !== 'all') && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X size={14} className="mr-1" />
                Clear
              </Button>
            )}

            <div className="flex-1" />

            <Button variant="outline" onClick={() => setShowImportModal(true)} data-testid="import-users-btn">
              <Upload size={16} className="mr-2" />
              Import Users
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => setShowAddUserModal(true)}
              data-testid="add-user-btn"
            >
              <UserPlus size={16} className="mr-2" />
              Add User
            </Button>
          </div>

          {selectedUsers.length > 0 && (
            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <Checkbox
                checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                onCheckedChange={(checked) => {
                  setSelectedUsers(checked ? [...filteredUsers] : []);
                }}
                data-testid="select-all-users"
              />
              <span className="text-sm font-medium text-amber-800">
                {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex-1" />
              <Button
                variant="outline"
                size="sm"
                className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => setBulkDeleteOpen(true)}
                data-testid="bulk-delete-btn"
              >
                <Trash2 size={14} className="mr-1" />
                Delete Selected
              </Button>
            </div>
          )}
        </div>

        {filteredUsers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="w-10 px-4 py-3" />
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">User</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Phone</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.map((user) => {
                  const isSelected = selectedUsers.some((u) => u.user_id === user.user_id);

                  return (
                    <tr
                      key={user.user_id}
                      className={`hover:bg-slate-50 ${isSelected ? 'bg-amber-50/50' : ''}`}
                      data-testid={`user-row-${user.user_id}`}
                    >
                      <td className="px-4 py-3">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedUsers([...selectedUsers, user]);
                            } else {
                              setSelectedUsers(selectedUsers.filter((u) => u.user_id !== user.user_id));
                            }
                          }}
                          data-testid={`select-user-${user.user_id}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-stone-900">{user.name || 'N/A'}</p>
                          <p className="text-xs text-slate-500">{user.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{user.phone || '-'}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            ['owner', 'specialist', 'staff'].includes(user.role)
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-emerald-100 text-emerald-700'
                          }`}
                        >
                          {user.role?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {user.status === 'archived' ? (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
                            Archived
                          </span>
                        ) : (
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              user.is_active
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-slate-100 text-slate-500'
                            }`}
                          >
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingUser(user);
                              setEditUserDrawerOpen(true);
                            }}
                            className="text-slate-600 border-slate-200 hover:bg-slate-50"
                            data-testid={`edit-user-${user.user_id}`}
                          >
                            <Pencil size={14} className="mr-1" />
                            Edit
                          </Button>

                          {enableSupportClient &&
                            ['self_repair_customer', 'managed_client'].includes(user.role) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSupportClient(user.user_id, user.name)}
                                disabled={contextLoading}
                                data-testid={`support-client-${user.user_id}`}
                              >
                                <Eye size={14} className="mr-1" />
                                View
                              </Button>
                            )}

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setUserToDelete(user);
                              setDeleteConfirmOpen(true);
                            }}
                            className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-user-${user.user_id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <Users size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">
              {users.length > 0 ? 'No users match your filters' : 'No users in this agency'}
            </p>
            {users.length > 0 && (
              <Button variant="link" onClick={clearFilters} className="mt-2">
                Clear filters
              </Button>
            )}
          </div>
        )}
      </div>

      <EditUserDrawer
        isOpen={editUserDrawerOpen}
        onClose={() => {
          setEditUserDrawerOpen(false);
          setEditingUser(null);
        }}
        user={editingUser}
        agencyId={agencyId}
        agencyName={agencyName}
        onSave={() => {
          refreshUsers();
          setEditUserDrawerOpen(false);
          setEditingUser(null);
        }}
      />

      <ImportUsersModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        agencyId={agencyId}
        agencyName={agencyName}
        onSuccess={() => {
          refreshUsers();
          setShowImportModal(false);
        }}
      />

      <AddUserModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        agencyId={agencyId}
        agencyName={agencyName}
        onSuccess={() => {
          refreshUsers();
          setShowAddUserModal(false);
        }}
      />

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-red-500" size={20} />
              Delete User
            </DialogTitle>
            <DialogDescription className="space-y-3">
              <p>
                Are you sure you want to delete <strong>{userToDelete?.name || userToDelete?.email}</strong>?
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <p className="font-medium mb-1">Note:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>
                    Users with linked records will be <strong>archived</strong> instead of permanently deleted
                  </li>
                  <li>Protected accounts cannot be deleted</li>
                  <li>Archived users can be restored by an administrator</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDeleteUser(userToDelete)}
              data-testid="confirm-delete-user-btn"
            >
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-red-500" size={20} />
              Delete {selectedUsers.length} Users
            </DialogTitle>
            <DialogDescription>
              Users with linked records will be archived instead of permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-40 overflow-y-auto border rounded-lg p-2 my-4">
            {selectedUsers.map((u) => (
              <div
                key={u.user_id}
                className="text-sm py-1 border-b last:border-0 flex items-center justify-between"
              >
                <span>{u.name || u.email}</span>
                <span className="text-xs text-slate-500">{u.role}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleBulkDelete} data-testid="confirm-bulk-delete-btn">
              Delete {selectedUsers.length} Users
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgencyUsersManagement;
